import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  ShoppingCart,
  Truck,
  Package,
  Plus,
  ChevronRight,
  ChevronLeft,
  UploadIcon,
  CameraIcon,
  ShieldCheck,
  Pill,
  FileScan,
  Store,
  CheckCircle,
  Star,
  Trash2,
  Search,
  Mic,
} from 'lucide-react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BottomNavigation from '../components/bottom_navigation';
import FeatureHeader from '../components/FeatureHeader';
import MedicationModal from '../components/medicationmodal';
import SearchBox from '../components/epharmacy_searchbox';
import { motion } from "framer-motion";
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import scan from '../assets/icons/scan.png';
import Logo from '../assets/logo.png';
import fifteen from '../assets/icons/15.png';
import sixteen from '../assets/icons/16.png';
import thirteen from "../assets/icons/13.png";
import { border, borderTop, padding } from '@mui/system';
import { opacity } from '@cloudinary/url-gen/actions/adjust';

// Optimized: Use require for images to enable bundler optimizations
const IMAGES = {
  categories: {
    purple: require("../assets/icons/fever.png"),
    women: require("../assets/icons/pregnant.png"),
    cold: require("../assets/icons/cough.png"),
    chronic: require("../assets/icons/heart.png"),
    digestive: require("../assets/icons/intestine.png"),
    skin: require("../assets/icons/skincare.png"),
    firstaid: require("../assets/icons/first-aid-kit.png"),
    malaria: require("../assets/icons/mosquito.png")
  },
  banners: {
    b1: require("../assets/banner/Card 1.0.jpg"),
    b2: require("../assets/banner/Card 2.0.jpg"),
    b3: require("../assets/banner/card 3.0.jpg")
  }
};

  const handleAddToCart = async (productId, selectedDose, quantity) => {
    

    try {
     
      const response = await api.post("/api/cart/add", {
        medicineId: productId,
        dose: selectedDose,
        quantity: quantity,
      });

      // Show success animation or notification
      showAddToCartSuccess();
      console.log("Cart response:", response.data);
    } catch (err) {
      console.error(err.response?.data || err.message);
      alert("Failed to add to cart. Make sure you are logged in!");
    } finally {
      
    }
  };

  const showAddToCartSuccess = () => {
    const successEl = document.createElement("div");
    successEl.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #10B981, #059669);
      color: white;
      padding: 16px 24px;
      border-radius: 12px;
      z-index: 9999;
      box-shadow: 0 8px 32px rgba(16, 185, 129, 0.3);
      animation: slideIn 0.3s ease;
      font-weight: 600;
    `;
    successEl.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        Added to cart successfully!
      </div>
    `;
    document.body.appendChild(successEl);
    setTimeout(() => {
      successEl.style.animation = "slideOut 0.3s ease forwards";
      setTimeout(() => document.body.removeChild(successEl), 300);
    }, 2000);
  };
// Memoized categories config
const categories = [
  { label: 'Fever and Pain Care', imgKey: 'purple' },
  { label: 'Malaria Care', imgKey: 'malaria' },
  { label: "Women’s Health", imgKey: 'women' },
  { label: "Cold, Flu and Cough", imgKey: 'cold' },
  { label: "Chronic Care", imgKey: 'chronic' },
  { label: "Digestive and Stomach Care", imgKey: 'digestive' },
  { label: "Skin and Wound Care", imgKey: 'skin' },
  { label: 'First Aid and Emergency', imgKey: 'firstaid' },
];

const categoryKeywords = {
  All: [],
  'Fever and Pain Care': ['fever', 'pain', 'paracetamol', 'ibuprofen', 'analgesic', 'headache'],
  'Malaria Care': ['malaria', 'mosquito', 'antimalarial', 'artemether', 'lumefantrine'],
  "Women’s Health": ['women', 'prenatal', 'pregnancy', 'fertility', 'family planning', 'feminine'],
  'Cold, Flu and Cough': ['cold', 'flu', 'cough', 'sore throat', 'congestion'],
  'Chronic Care': ['chronic', 'diabetes', 'hypertension', 'heart', 'bp', 'pressure', 'asthma'],
  'Digestive and Stomach Care': ['digestive', 'stomach', 'ulcer', 'antacid', 'diarrhea', 'constipation', 'nausea'],
  'Skin and Wound Care': ['skin', 'wound', 'cream', 'ointment', 'rash', 'antiseptic'],
  'First Aid and Emergency': ['first aid', 'emergency', 'bandage', 'gauze', 'plaster', 'injury'],
};

const normalizeCategoryText = (value = '') => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const productMatchesCategory = (product, activeCategory) => {
  if (!activeCategory || activeCategory === 'All') return true;

  const haystack = normalizeCategoryText([
    product?.name,
    product?.category,
    product?.type,
    product?.brand,
    product?.description,
  ].filter(Boolean).join(' '));

  const normalizedCategory = normalizeCategoryText(activeCategory);
  if (haystack.includes(normalizedCategory)) return true;

  return (categoryKeywords[activeCategory] || []).some((keyword) => haystack.includes(normalizeCategoryText(keyword)));
};

const promoImages = [IMAGES.banners.b1, IMAGES.banners.b2, IMAGES.banners.b3];

const trustHighlights = [
  {
    Icon: ShieldCheck,
    title: 'Verified & Licensed',
    subtitle: 'Trusted pharmacies',
  },
  {
    Icon: Pill,
    title: 'Pharmacist Reviewed',
    subtitle: 'Every prescription',
  },
  {
    Icon: CheckCircle,
    title: '100% Genuine',
    subtitle: 'No counterfeit drugs',
  },
];

export default function EPharmacy() {
  const navigate = useNavigate();
  const [showPharmacyMedications, setShowPharmacyMedications] = useState(false);
  const [selectedMedication, setSelectedMedication] = useState(null);
  
  // Single optimized state object to minimize re-renders
  const [state, setState] = useState({
    pharmacy: [],
    likeable_product: [],
    featured: [],
    orderStatus: [],
    loading: true,
    collapsed: true,
    isPaused: false,
    searchQuery: '',
    activeCategory: 'All'
  });
  
  const carouselRef = useRef(null);

  const allProducts = useMemo(() => {
    const seen = new Set();
    return [...state.likeable_product, ...state.featured].filter((product) => {
      const key = product?._id;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [state.likeable_product, state.featured]);

  const filteredProducts = useMemo(() => {
    return allProducts.filter((product) => productMatchesCategory(product, state.activeCategory));
  }, [allProducts, state.activeCategory]);

  const openMedicationForm = (item) => {
    setSelectedMedication({
      medicineName: item.name,
      brandName: item.brandName || item.brand || '',
      genericName: item.genericName || '',
      strength: item.dosage || '',
      category: item.category || '',
    });
    setShowPharmacyMedications(false);
  };

  const saveMedication = async (medicationData) => {
    try {
      await api.post('/api/medications', medicationData);
      setSelectedMedication(null);
      alert(`${medicationData.medicineName} added to your medications.`);
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to add medication. Please sign in.');
    }
  };

  // Memoized calculations to prevent re-computation
  const statusCounts = useMemo(() => {
    return state.orderStatus.reduce(
      (acc, order) => {
        if (order.status === "Pending") acc.Pending += 1;
        else if (order.status === "Completed") acc.Completed += 1;
        else if (order.status === "Processing") acc.Processing += 1;
        return acc;
      },
      { Pending: 0, Completed: 0, Processing: 0 }
    );
  }, [state.orderStatus]);

  // Optimized: Single useEffect with parallel API calls
  useEffect(() => {
    let isMounted = true;
    let controller = new AbortController();
    
    const fetchAllData = async () => {
      try {
        // Keep pharmacy products visible even if protected endpoints fail (e.g., orders when logged out).
        const [pharmacyResult, medicinesResult, ordersResult, featuredResult] =
          await Promise.allSettled([
            api.get("/api/pharmacy", { signal: controller.signal }),
            api.get('api/medicines/best-sellers', { signal: controller.signal }),
            api.get("/api/orders", { signal: controller.signal }),
            api.get("api/medicines/featured", { signal: controller.signal })
          ]);

        const pharmacy =
          pharmacyResult.status === 'fulfilled' && Array.isArray(pharmacyResult.value?.data)
            ? pharmacyResult.value.data
            : [];

        const likeableProduct =
          medicinesResult.status === 'fulfilled'
            ? medicinesResult.value?.data?.bestSellers || []
            : [];

        const orderStatus =
          ordersResult.status === 'fulfilled' && Array.isArray(ordersResult.value?.data)
            ? ordersResult.value.data
            : [];

        const featured =
          featuredResult.status === 'fulfilled'
            ? featuredResult.value?.data?.featured || []
            : [];
        
        if (isMounted) {
          setState(prev => ({
            ...prev,
            pharmacy,
            likeable_product: likeableProduct,
            orderStatus,
            featured,
            loading: false
          }));
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching data:', error);
        if (isMounted) {
          setState(prev => ({ ...prev, loading: false }));
        }
      }
    };
    
    fetchAllData();
    
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // Optimized carousel effect with cleanup
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel || state.isPaused) return;
    
    const interval = setInterval(() => {
      if (!carousel || !carousel.firstChild) return;

      const gap = parseFloat(window.getComputedStyle(carousel).gap || '0') || 0;
      const cardWidth = carousel.firstChild.getBoundingClientRect().width + gap;
      if (carousel.scrollLeft + carousel.offsetWidth >= carousel.scrollWidth) {
        carousel.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        carousel.scrollBy({ left: cardWidth, behavior: "smooth" });
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [state.isPaused]);

  // Memoized event handlers
  const scrollLeft = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel || !carousel.firstChild) return;
    const gap = parseFloat(window.getComputedStyle(carousel).gap || '0') || 0;
    const cardWidth = carousel.firstChild.getBoundingClientRect().width + gap;
    carousel.scrollBy({ left: -cardWidth, behavior: "smooth" });
  }, []);

  const scrollRight = useCallback(() => {
    const carousel = carouselRef.current;
    if (!carousel || !carousel.firstChild) return;
    const gap = parseFloat(window.getComputedStyle(carousel).gap || '0') || 0;
    const cardWidth = carousel.firstChild.getBoundingClientRect().width + gap;
    if (carousel.scrollLeft + carousel.offsetWidth >= carousel.scrollWidth) {
      carousel.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      carousel.scrollBy({ left: cardWidth, behavior: "smooth" });
    }
  }, []);

  const toggleCollapsed = useCallback(() => {
    setState(prev => ({ ...prev, collapsed: !prev.collapsed }));
  }, []);

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);



 return (
  <div style={styles.container}>
    <div className='container'>
      <Header
        navigate={navigate}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        carouselRef={carouselRef}
        promoImages={promoImages}
        scrollLeft={scrollLeft}
        scrollRight={scrollRight}
        setIsPaused={(value) => setState((prev) => ({ ...prev, isPaused: value }))}
        onOpenMedications={() => setShowPharmacyMedications(true)}
      />

      <div style={styles.content}>
      <MedicineCategories
        activeCategory={state.activeCategory}
        onChangeCategory={(category) => setState((prev) => ({ ...prev, activeCategory: category }))}
      />

      <ProductSection
        title={state.activeCategory === 'All' ? 'All Products' : state.activeCategory

        }
        products={filteredProducts}
        loading={state.loading}
        navigate={navigate}
        seeAllPath={state.activeCategory === 'All' ? '/all-products' : `/category/${encodeURIComponent(state.activeCategory)}`}
      />

      <ProductSection
        title="Trending Products"
        products={state.likeable_product}
        loading={state.loading}
        navigate={navigate}
        seeAllPath="/trending"
      />

      <ProductSection
        title="Featured Products"
        products={state.featured}
        loading={state.loading}
        navigate={navigate}
        seeAllPath="/featured"
      />

      <OrderSection
        orderStatus={state.orderStatus}
        statusCounts={statusCounts}
        collapsed={state.collapsed}
        toggleCollapsed={toggleCollapsed}
      />

      <SafetyBanner />

      <PharmacySection
        pharmacy={state.pharmacy}
        navigate={navigate}
      />
      </div>
    </div>

    {showPharmacyMedications && (
      <div style={styles.modalBackdrop} onClick={() => setShowPharmacyMedications(false)}>
        <div style={styles.pharmacyMedicationModal} onClick={(event) => event.stopPropagation()}>
          <div style={styles.modalHeader}>
            <div>
              <h3 style={styles.modalTitle}>Pharmacy medications</h3>
              <p style={styles.modalSubtitle}>Add a drug to your medication schedule.</p>
            </div>
            <button type="button" aria-label="Close pharmacy medications" style={styles.modalClose} onClick={() => setShowPharmacyMedications(false)}>
              ×
            </button>
          </div>
          <div style={styles.pharmacyMedicationList}>
            {allProducts.map((item) => (
              <div key={item._id} style={styles.pharmacyMedicationRow}>
                <div style={styles.pharmacyMedicationInfo}>
                  <strong>{item.name}</strong>
                  <span>{item.dosage || item.type || item.category || 'Medication'}</span>
                </div>
                <button type="button" style={styles.addMedicationButton} onClick={() => openMedicationForm(item)}>
                  <Plus size={15} /> Add
                </button>
              </div>
            ))}
            {allProducts.length === 0 && <p style={styles.emptyText}>No pharmacy medications available.</p>}
          </div>
        </div>
      </div>
    )}

    <MedicationModal
      open={Boolean(selectedMedication)}
      onClose={() => setSelectedMedication(null)}
      onSave={saveMedication}
      mode="add"
      initialMedication={selectedMedication}
    />


  </div>
);

}

// Extracted Components with React.memo for performance

const LoadingSkeleton = React.memo(() => (
  <div style={styles.container}>
    <div style={{ padding: 20 }}>
      {/* Skeleton Header */}
      <div style={{ 
        height: 60, 
        background: 'var(--surface)', 
        borderRadius: 12, 
        marginBottom: 16,
        animation: 'shimmer 1.5s infinite',
        backgroundSize: '200% 100%'
      }}></div>
      
      {/* Skeleton Categories */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {[...Array(4)].map((_, i) => (
          <div 
            key={i} 
            style={{ 
              width: 80, 
              height: 80, 
              background: 'var(--surface)', 
              borderRadius: 12,
              animation: 'shimmer 1.5s infinite',
              backgroundSize: '200% 100%'
            }}
          ></div>
        ))}
      </div>
      
      {/* Skeleton Products */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        {[...Array(2)].map((_, i) => (
          <div 
            key={i} 
            style={{ 
              flex: 1, 
              height: 200, 
              background: 'var(--surface)', 
              borderRadius: 12,
              animation: 'shimmer 1.5s infinite',
              backgroundSize: '200% 100%'
            }}
          ></div>
        ))}
      </div>
    </div>
  </div>
));

const Header = React.memo(({ navigate, handleMouseEnter, handleMouseLeave, carouselRef, promoImages, scrollLeft, scrollRight, setIsPaused, onOpenMedications }) => {
  const trustStripRef = useRef(null);
  const [isTrustStripPaused, setIsTrustStripPaused] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const recognitionRef = useRef(null);

  const handleMicClick = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Speech recognition is not supported in this browser.'); return; }
    if (isListening && recognitionRef.current) { recognitionRef.current.stop(); return; }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognitionRef.current = recognition;
    recognition.onstart = () => setIsListening(true);
    recognition.onend = () => setIsListening(false);
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setSearchQuery(transcript);
    };
    recognition.start();
  };

  useEffect(() => {
    const strip = trustStripRef.current;
    if (!strip || isTrustStripPaused) return;

    const interval = setInterval(() => {
      const resetPoint = strip.scrollWidth / 2;
      if (resetPoint <= 0) return;

      if (strip.scrollLeft >= resetPoint) {
        strip.scrollLeft -= resetPoint;
      }

      strip.scrollBy({ left: 1, behavior: 'auto' });
    }, 24);

    return () => clearInterval(interval);
  }, [isTrustStripPaused]);

  return (
  <div style={styles.header}>
    <FeatureHeader
      title="Pharmacy"
      subtitle="Safe, verified medicines delivered to you"
      onBack={() => navigate(-1)}
      rightSlot={(
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={styles.cartIcon}
            onClick={onOpenMedications}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            role="button"
            tabIndex={0}
            aria-label="Open pharmacy medications"
            title="Add to my medications"
          >
            <Pill size={22} color="var(--on-bg)" />
          </div>
          <div
            style={styles.cartIcon}
            onClick={() => navigate("/cart")}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <ShoppingCart size={22} color="var(--on-bg)" />
            <span style={styles.cartBadge}></span>
          </div>
        </div>
      )}
      containerStyle={{ padding: 0 }}
    />
    {/* Trust Strip 
    <div
      ref={trustStripRef}
      className="trust-strip-scroll"
      style={styles.trustStrip}
      onMouseEnter={() => setIsTrustStripPaused(true)}
      onMouseLeave={() => setIsTrustStripPaused(false)}
      onTouchStart={() => setIsTrustStripPaused(true)}
      onTouchEnd={() => setIsTrustStripPaused(false)}
    >
      <div style={styles.trustTrack}>
        {[...trustHighlights, ...trustHighlights].map((item, index, list) => {
          const Icon = item.Icon;
          return (
            <React.Fragment key={`${item.title}-${index}`}>
              <div style={styles.trustItem}>
                <div style={styles.trustItemIconWrap}>
                  <Icon size={13} color="var(--primary)" />
                </div>
                <div style={styles.trustItemCopy}>
                  <span style={styles.trustItemTitle}>{item.title}</span>
                  <span style={styles.trustItemSubtitle}>{item.subtitle}</span>
                </div>
              </div>
              {index < list.length - 1 && <div style={styles.trustStripDivider}></div>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
    */}
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: "10px" }}>
      {/* Shared capsule for AI + search */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 6,
          height: 64,
          borderRadius: 32,
          padding: "6px 10px",
          background: "#f7f4ff",
          border: "1px solid #ece5ff",
          boxShadow: "0 2px 8px rgba(124,77,255,0.08)",
        }}
      >
        <div
          onClick={() => navigate('/asha')}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            background: "#efe8ff",
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <AutoAwesomeIcon style={{ fontSize: 20, color: "var(--primary)" }} />
        </div>

        {/* Search box */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            gap: 8,
            position: "relative",
            background: "#fff",
            border: "1px solid #eee8ff",
            borderRadius: 26,
            padding: "0 46px 0 12px",
            height: 50, 
          }}
        >
          <Search size={17} color="var(--primary)" style={{ flexShrink: 0 }} />
          <input
            type="text"
            placeholder="What do you need help with?"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                navigate(searchQuery.trim() ? `/search?q=${encodeURIComponent(searchQuery.trim())}` : '/search');
              }
            }}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 10,
              color: "#8a8a95",
            }}
          />
          <div
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              height: 22,
              paddingLeft: 10,
              borderLeft: "1px solid #e6e6e8",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Mic
              size={19}
              color="var(--primary)"
              style={{ cursor: "pointer" }}
              onClick={handleMicClick}
            />
          </div>
        </div>
      </div>

      {/* Upload Rx */}
      <button
        type="button"
        aria-label="Upload Prescription"
        onClick={() => navigate("/prescription")}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          width: 40,
          height: 40,
          borderRadius: "50% ",
          border: "1px solid #ece5ff",
          background: "#fff",
          color: "var(--primary)",
          cursor: "pointer",
          flexShrink: 0,
          padding: "2px 0",
          boxShadow: "0 2px 6px rgba(124,77,255,0.06)",
        }}
      >
        <UploadIcon size={18} strokeWidth={2} />
        <span style={{ fontSize: 9, fontWeight: 600, color: "#1a1a2e", lineHeight: 1.1, textAlign: "center" }}>
       
        </span>
      </button>

      {/* Scan Now */}
      <button
        type="button"
        aria-label="Scan Prescription"
        onClick={() => navigate("/prescription")}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "1px solid #ece5ff",
          background: "#fff",
          color: "var(--primary)",
          cursor: "pointer",
          flexShrink: 0,
          padding: "2px 0",
          boxShadow: "0 2px 6px rgba(124,77,255,0.06)",
        }}
      >
        <CameraIcon size={18} strokeWidth={2} />
        <span style={{ fontSize: 9, fontWeight: 600, color: "#1a1a2e", lineHeight: 1.1, textAlign: "center" }}>
     
        </span>
      </button>
    </div>

    <PromoCarousel
      carouselRef={carouselRef}
      promoImages={promoImages}
      scrollLeft={scrollLeft}
      scrollRight={scrollRight}
      setIsPaused={setIsPaused}
    />

   
  </div>
  );
});

const QuickActions = React.memo(({ handleMouseEnter, handleMouseLeave }) => (
  <div style={styles.quickActions}>
    {[
      { icon: <UploadIcon size={26} color="white" />, label: "Upload Prescription" },
      { icon: <CameraIcon size={26} color="white" />, label: "Scan Prescription" },
    ].map((item, idx) => (
      <div
        key={idx}
        style={styles.actionCard}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div>{item.icon}</div>
        <h6 style={styles.actionText}>{item.label}</h6>
      </div>
    ))}
  </div>
));

const CategorySection = React.memo(({ categories, navigate }) => (
  <section style={styles.section}>
    <div style={styles.sectionHeader}>
      <h6 style={styles.sectionTitle}>Browse Categories</h6>
      <button 
        style={styles.seeAllButton}
        onClick={() => navigate('/all-categories')}
      >
        See All <ChevronRight size={16} />
      </button>
    </div>

    <div style={styles.categoriesGrid}>
      {categories.map((category, index) => (
        <motion.div
          key={category.label}
          style={styles.categoryCard}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate(`/category/${category.label}`)}
        >
          <div style={styles.categoryImageWrapper}>
            <img
              src={IMAGES.categories[category.imgKey]}
              alt={category.label}
              style={styles.categoryImage}
              loading="lazy"
            />
          </div>
          <span style={styles.categoryLabel}>{category.label}</span>
        </motion.div>
      ))}
    </div>
  </section>
));

const ProductSection = React.memo(({ title, products, loading, navigate, seeAllPath }) => {
  const firstRow = products.filter((_, index) => index % 2 === 0);
  const secondRow = products.filter((_, index) => index % 2 === 1);

  return (
    <section style={styles.section}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.sectionTitle}>{title}</h2>
        <button 
          style={styles.seeAllButton}
          onClick={() => navigate(seeAllPath)}
        >
          See All <ChevronRight size={16} />
        </button>
      </div>

      {loading ? (
        <div style={styles.productsRowsWrap}>
          <div style={styles.productsRow}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={`skeleton-r1-${i}`} style={styles.productCardSkeleton}>
                <div style={styles.skeletonImage}></div>
                <div style={styles.skeletonText}></div>
                <div style={styles.skeletonText}></div>
              </div>
            ))}
          </div>
          <div style={styles.productsRow}>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={`skeleton-r2-${i}`} style={styles.productCardSkeleton}>
                <div style={styles.skeletonImage}></div>
                <div style={styles.skeletonText}></div>
                <div style={styles.skeletonText}></div>
              </div>
            ))}
          </div>
        </div>
      ) : products.length === 0 ? (
        <div style={styles.emptyProductsState}>
          No products found for this category yet.
        </div>
      ) : (
        <div style={styles.productsRowsWrap}>
          <div style={styles.productsRow}>
            {firstRow.map((product) => (
              <ProductCard key={`row1-${product._id}`} product={product} />
            ))}
          </div>
          <div style={styles.productsRow}>
            {secondRow.map((product) => (
              <ProductCard key={`row2-${product._id}`} product={product} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
});

const ProductCard = React.memo(({ product }) => {
  const navigate = useNavigate();

  return (
    <motion.div
      style={styles.productCard}
      whileHover={{ y: -4 }}
      whileTap={{ scale: 0.95 }}
    >
      <div onClick={() => navigate(`/product/${product._id}`)}>
      <div style={styles.productImageContainer}>
        <img
          src={product.images?.[0] || ''}
          alt={product.name}
          style={styles.productImage}
          loading="lazy"
        />

        <div style={{
          ...styles.prescriptionBadge,
          backgroundColor: product.prescriptionRequired ? '#7914f450' : ''
        }}>
          {product.prescriptionRequired ? "Prescription Required" : ""}
        </div>

          <motion.button
            style={styles.cartButton}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.92 }}
            onClick={(e) => {
              e.stopPropagation();
              handleAddToCart(product._id, "0mg", 1);
            }}
            aria-label={`Add ${product.name} to cart`}
          >
            <Plus size={16} />
          </motion.button>
      </div>

      <div style={styles.productInfo}>
        <h6 style={styles.productName}>{product.name}</h6>
     
        <div style={styles.priceContainer}>
          {product.originalPrice > product.price && (
            <span style={styles.originalPrice}>${product.originalPrice}</span>
          )}
          <span style={styles.currentPrice}>${product.price}</span>
        </div>
      </div>
      </div>
    </motion.div>
  );
});

const OrderSection = React.memo(({ orderStatus, statusCounts, collapsed, toggleCollapsed }) => (
  <section style={{ marginTop: 24 }}>
    <div
      style={styles.orderHeader}
      onClick={toggleCollapsed}
      onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-1px)"}
      onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
    >
      <div style={styles.orderHeaderTop}>
        <span style={styles.orderHeaderTitle}>You have {orderStatus.length} active orders</span>
        <span style={styles.orderHeaderToggle}>
          {collapsed ? "→ View" : "↓ Hide"}
        </span>
      </div>

      <div style={styles.statusSummary}>
        {statusCounts.Pending > 0 && (
          <span style={{ ...styles.orderStatusPill, color: "#9a6700", background: "#fff5da" }}>
            {statusCounts.Pending} Pending
          </span>
        )}
        {statusCounts.Completed > 0 && (
          <span style={{ ...styles.orderStatusPill, color: "#0f7a55", background: "#dcfce7" }}>
            {statusCounts.Completed} Completed
          </span>
        )}
        {statusCounts.Processing > 0 && (
          <span style={{ ...styles.orderStatusPill, color: "#1d4ed8", background: "#e5efff" }}>
            {statusCounts.Processing} Processing
          </span>
        )}
      </div>
    </div>

    <motion.div
      initial={false}
      animate={{
        height: collapsed ? 0 : "auto",
        opacity: collapsed ? 0 : 1,
      }}
      style={{ overflow: "hidden" }}
      transition={{ duration: 0.3 }}
    >
      <div style={{ marginTop: 16 }}>
        <div style={styles.orderSectionHeader}>
          <Package size={20} style={{ marginRight: 8 }} />
          <h2 style={{ fontSize: 16, margin: 0 }}>Recent Orders</h2>
        </div>

        <div style={styles.ordersList}>
          {orderStatus.slice(0, 3).map((order) => (
            <OrderItem key={order._id} order={order} />
          ))}
        </div>
      </div>
    </motion.div>
  </section>
));

const OrderItem = React.memo(({ order }) => (
  <motion.div
    style={styles.orderItem}
    whileHover={{ y: -2 }}
  >
    <div style={styles.orderItemHeader}>
      <div>
        <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>#{order._id.slice(-6)}</h4>
        <p style={styles.orderDate}>
          {new Date(order.createdAt).toLocaleDateString()}
        </p>
      </div>
      <span style={{
        ...styles.orderStatus,
        background: order.status === "Completed" ? "#10B981" :
                   order.status === "Processing" ? "#3B82F6" :
                   order.status === "Pending" ? "#F59E0B" : "#EF4444",
      }}>
        {order.status}
      </span>
    </div>

    <div style={styles.orderItems}>
      {order.items.map((item) => (
        <div key={item._id} style={styles.orderItemRow}>
          <div style={styles.orderItemImageWrapper}>
            {item.medicine?.images?.[0] && (
              <img
                src={item.medicine.images[0]}
                alt={item.medicine.name}
                style={styles.orderItemImage}
                loading="lazy"
              />
            )}
          </div>

          <div style={styles.orderItemDetails}>
            <h6 style={styles.orderItemName}>{item.medicine?.name || item.name}</h6>
            <p style={styles.orderItemSubtext}>
              {item.medicine?.quantity || "12 glass"} • {item.medicine?.category || "Category"}
            </p>
            <p style={styles.orderItemPrice}>${item.price.toFixed(2)}</p>
          </div>

          <div style={styles.orderItemControl}>
            <span style={styles.orderItemQty}>{item.quantity}</span>
            <button style={styles.orderItemAction}>
              <Plus size={16} color="#fff" />
            </button>
          </div>

          <button style={styles.orderItemRemove}>
            <Trash2 size={14} color="var(--primary)" />
          </button>
        </div>
      ))}
    </div>

    <div style={styles.orderFooter}>
      <span style={{ fontWeight: 600, fontSize: 13 }}>
        Total: ${order.totalPrice.toFixed(2)}
      </span>
      <Truck size={16} color="#6B7280" />
    </div>
  </motion.div>
));

const PromoCarousel = React.memo(({ carouselRef, promoImages, scrollLeft, scrollRight, setIsPaused }) => {
  const [activeSlide, setActiveSlide] = useState(0);

  const handleCarouselScroll = (event) => {
    const element = event.currentTarget;
    const index = Math.round(element.scrollLeft / element.offsetWidth);
    setActiveSlide(index);
  };

  const scrollToSlide = (index) => {
    const carousel = carouselRef.current;
    if (!carousel) return;
    carousel.scrollTo({ left: index * carousel.offsetWidth, behavior: "smooth" });
  };

  return (
    <div
      style={styles.carouselContainer}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <button type="button" aria-label="Previous banner" style={{ ...styles.arrowButton, ...styles.leftArrow }} onClick={scrollLeft}>
        <ChevronLeft size={24} />
      </button>

      <button type="button" aria-label="Next banner" style={{ ...styles.arrowButton, ...styles.rightArrow }} onClick={scrollRight}>
        <ChevronRight size={24} />
      </button>

      <div style={styles.promoCarousel} ref={carouselRef} onScroll={handleCarouselScroll}>
        {promoImages.map((img, idx) => (
          <div key={idx} style={styles.promoBanner}>
            <img src={img} alt={`Card ${idx + 1}.0 banner`} style={styles.promoImage} loading="lazy" />
          </div>
        ))}
      </div>

      <div style={styles.promoDotsWrap}>
        {promoImages.map((_, index) => (
          <span
            key={`promo-dot-${index}`}
            onClick={() => scrollToSlide(index)}
            style={{
              ...styles.promoDot,
              ...(activeSlide === index ? styles.promoDotActive : {}),
            }}
          />
        ))}
      </div>
    </div>
  );
});

const SafetyBanner = React.memo(() => (
  <div style={styles.safetyBanner}>
    <div style={styles.safetyContent}>
      <div style={styles.safetyIcon}>
        <ShieldCheck size={22} />
      </div>
      <div>
        <h3 style={styles.safetyTitle}>Medication Safety Reminder</h3>
        <p style={styles.safetyText}>
          Always follow dosage instructions. <br />
          Consult a doctor if symptoms persist.
        </p>
      </div>
    </div>

    <button
      style={styles.learnMoreButton}
      onMouseEnter={(e) => e.currentTarget.style.background = "var(--primary)"}
      onMouseLeave={(e) => e.currentTarget.style.background = "#  "}
    >
      Learn More
    </button>
  </div>
));

const PharmacySection = React.memo(({ pharmacy, navigate }) => (
  <section style={styles.section}>
    <div style={styles.sectionHeader}>
      <h2 style={styles.sectionTitle}>
        <Store size={20} style={{ marginRight: 8 }} />
        Nearby Pharmacies
      </h2>
      <button 
        style={styles.seeAllButton}
        onClick={() => navigate('/all-pharmacies')}
      >
        See All <ChevronRight size={16} />
      </button>
    </div>
    <div style={styles.pharmaciesGrid}>
      {pharmacy.map((pharm, idx) => (
        <PharmacyCard key={idx} pharmacy={pharm} navigate={navigate} />
      ))}
    </div>
  </section>
));

const PharmacyCard = React.memo(({ pharmacy, navigate }) => (
  <motion.div 
    style={styles.pharmacyCard}
    whileHover={{ y: -4 }}
    onClick={() => navigate(`/pharmacy/${pharmacy._id}`)}
  >
    <div style={styles.pharmacyLogo}>
      <img 
        src={pharmacy.logo || pharmacy.image || 'https://via.placeholder.com/360x220'} 
        alt={pharmacy.name}
        style={styles.pharmacyLogoImage}
        loading="lazy"
      />
      {pharmacy.verified && (
        <div style={styles.verifiedBadge}>
          <CheckCircle size={12} />
        </div>
      )}
    </div>

    <div style={styles.pharmacyInfoPanel}>
      <h4 style={styles.pharmacyName}>{pharmacy.name}</h4>
      <div style={styles.pharmacyRating}>
        <Star size={12} fill="#FFD700" color="#FFD700" />
        <span style={styles.ratingText}>{pharmacy.rating || '4.5'}</span>
        <span style={styles.reviewsText}>({pharmacy.reviews || '100'})</span>
      </div>

      <div style={styles.deliveryBadge}>
        <Truck size={10} />
        <span>Free Delivery</span>
      </div>
    </div>
  </motion.div>
));



function MedicineCategories({ activeCategory, onChangeCategory }) {
  const navigate = useNavigate();
  const categoryOptions = [{ label: 'All' }, ...categories];

  const styles = {
   

    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 12,
      overflowX: "auto",
      gap : 8,
    },

    title: {
      fontSize: 14,
      fontWeight: 700,
      color: "#1f1f1f",
    },

    viewAll: {
      fontSize: 12,
      color: "#6a6a6a",
      cursor: "pointer",
    },

 list: {
  display: "flex",
  flexDirection: "row",
  overflowX: "auto",
  gap: 12,
},

 section: {
    marginTop: 24,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
    marginTop: -10,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--on-bg)",
  },
  seeAllButton: {
    background: 'none',
    border: 'none',
    color: 'var(--primary)',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
  },

   card: {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  backgroundColor: "var(--surface)",
  borderRadius: 30,
  height : 60,
    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
  fontSize: 12,
  fontWeight: 600,
  color: "#333",
  border: "1px solid #ededed",
},

    icon: {
      fontSize: 14,
      whiteSpace : "nowrap",
    },
  };

  return (
    <div >
    <div style={styles.sectionHeader}>
                     <h6 style={styles.sectionTitle}>Top Health Categories</h6>
                     <button 
                       style={styles.seeAllButton}
                        onClick={() => navigate('/all-categories')}
                     >
                       View All <ChevronRight size={16} />
                     </button>
                   </div>
   
   
     <div className="d-flex gap-2 overflow-auto mb-4">
       {categoryOptions.map((cat) => (
         <button
           key={cat.label}
           onClick={() => onChangeCategory(cat.label)}
           className="btn btn-light"
           style={{
             borderRadius: 16,
             fontSize: 12.5,
             padding: "6px 10px",
             whiteSpace: "nowrap",
             background: activeCategory === cat.label ? "var(--primary)" : "#fff",
             color: activeCategory === cat.label ? "#fff" : "#1f2937",
             border: activeCategory === cat.label ? "1px solid var(--primary)" : "1px solid #e5e7eb",
           }}
         >
          {cat.label}
         </button>
       ))}
     </div>
     </div>
   
  );
}


const MedicineOptions = () => {

  const navigate = useNavigate();
  const [isCompactMobile, setIsCompactMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 370;
  });

  useEffect(() => {
    const onResize = () => setIsCompactMobile(window.innerWidth < 370);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const container = {
    display: "flex",
    gap: 10,
    padding: "0 6px",
    justifyContent: "center",
    marginTop: 14,
    marginBottom: 14,
    alignItems: "stretch",
    flexDirection: isCompactMobile ? "column" : "row",
  };

  const card = {
    flex: isCompactMobile ? "1 1 100%" : "1 1 0",
    minHeight: 84,
    padding: "10px 12px",
    borderRadius: 18,
    border: "1px solid #e9e6f1",
    lineHeight: 1,
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "#ffffff",
    boxShadow: "0 3px 10px rgba(35, 22, 60, 0.07)",
    minWidth: 0,
    width: isCompactMobile ? "100%" : "auto",
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  };

  const iconWrap = {
    width: "clamp(30px, 8vw, 36px)",
    height: "clamp(30px, 8vw, 36px)",
    borderRadius: "50%",
    background: "var(--primary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const firstIconWrap = {
    ...iconWrap,
  };

  const copy = {
    flex: 1,
    minWidth: 0,
  };

  const title = {
    fontSize: "12px",
    fontWeight: 700,
    color: "#1f1a33",
    marginTop: 0,
    marginBottom: 0,
    lineHeight: 1.15,
    whiteSpace: "normal",
  };

  const subtitle = {
    marginTop: 4,
    marginBottom: 0,
    fontSize: "10px",
    color: "#4d4960",
    lineHeight: 1.24,
    fontWeight: 500,
    whiteSpace: "normal",
  };

  const button = {
    width: "auto",
    height: "auto",
    borderRadius: 0,
    border: "none",
    background: "transparent",
    color: "var(--primary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    padding: "0 2px 0 4px",
  };

  const handleCardClick = (route, e) => {
    e.preventDefault();
    navigate(route);
  };

  return (
    <div style={container}>
      <div
        style={card}
        onClick={(e) => handleCardClick("/asha", e)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 6px 16px rgba(35, 22, 60, 0.12)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 3px 10px rgba(35, 22, 60, 0.07)";
        }}
      >
        <div style={firstIconWrap}>
          <img
            src={thirteen}
            alt="Pills"
            style={{
              width: "150%",
              height: "150%",
              maxWidth: "150%",
              maxHeight: "150%",
              objectFit: "contain",
            }}
          />
        </div>

        <div style={copy}>
          <p style={title}>Get Medicine<br />Guidance</p>
        </div>

        <div style={button}>
          <ChevronRight size={12} />
        </div>
      </div>

      <div
        style={card}
        onClick={(e) => handleCardClick("/scan", e)}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = "0 6px 16px rgba(35, 22, 60, 0.12)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "0 3px 10px rgba(35, 22, 60, 0.07)";
        }}
      >
        <div style={iconWrap}>
          <CameraIcon size={13} color="#ffffff" strokeWidth={2} />
        </div>

        <div style={copy}>
          <p style={title}>Upload Prescription</p>
        </div>

        <div style={button}>
          <ChevronRight size={12} />
        </div>
      </div>
    </div>
  );
};

// Optimized Styles
const styles = {
  container: {
    minHeight: '100vh',
    background: '#f3f0f8',
    paddingBottom: '80px',
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 30,
    background: 'rgba(15, 23, 42, 0.48)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: 12,
  },
  pharmacyMedicationModal: {
    width: '100%',
    maxWidth: 430,
    maxHeight: '78vh',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: '18px 18px 8px 8px',
    padding: 18,
    boxShadow: '0 18px 50px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    marginBottom: 16,
  },
  modalTitle: {
    margin: 0,
    color: '#111827',
    fontSize: 19,
  },
  modalSubtitle: {
    margin: '4px 0 0',
    color: '#6b7280',
    fontSize: 13,
  },
  modalClose: {
    width: 34,
    height: 34,
    border: 'none',
    borderRadius: '50%',
    background: '#eef1f5',
    color: '#374151',
    fontSize: 22,
    lineHeight: 1,
    cursor: 'pointer',
  },
  pharmacyMedicationList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  pharmacyMedicationRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '12px 0',
    borderBottom: '1px solid #edf0f4',
  },
  pharmacyMedicationInfo: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    color: '#111827',
  },
  addMedicationButton: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    border: 'none',
    borderRadius: 999,
    padding: '8px 12px',
    background: 'var(--primary)',
    color: '#fff',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  emptyText: {
    color: '#7d8290',
    marginTop: 14,
    textAlign: 'center',
  },
  header: {
    background: "transparent",
    color: "#18141f",
    position: "relative",
    width: "100%",
    margin: "0 0 20px",
    padding: "18px 0 8px",
    overflow: "visible",
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    fontFamily: "'Poppins', sans-serif",
  },
  headerTop: {
    display: "flex",
    marginTop: 0,
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitleWrap: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  backArrow: {
    cursor: 'pointer',
    flexShrink: 0,
    fontSize: 22,
    fontWeight: 700,
    color: "var(--primary)",
    marginTop: 4,
  },
  headerTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 22,
    marginLeft: 12,
    color: "var(--on-bg)",
    letterSpacing: "0.3px",
    textAlign: "center",
    lineHeight: 1.15,
  },
  cartIcon: {
    position: "relative",
    width: 30,
    height: 30,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
    backdropFilter: "blur(10px)",
    transition: "all 0.2s",
  },
  cartBadge: {
    position: "absolute",
    top: 8,
    right: 10,
    width: 8,
    height: 8,
    background: "var(--primary)",
    borderRadius: "50%",
    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
  },
  headerSubtitle: {
    fontSize: 12,
    marginTop: 4,
    marginLeft: 34,
    textAlign: "left",
  },
  trustStrip: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 6,
    marginBottom: 10,
    marginTop: 8,
    background: "#ffffff",
    border: "1px solid #eceef1",
    borderRadius: 999,
    padding: "6px 8px",
    overflowX: "auto",
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },
  trustTrack: {
    display: "flex",
    alignItems: "center",
    width: "max-content",
    gap: 6,
  },
  trustItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    minWidth: 142,
  },
  trustItemIconWrap: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    border: "1px solid #eceef1",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  trustItemCopy: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.15,
  },
  trustItemTitle: {
    fontSize: 11,
    color: "#3D3A52",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  trustItemSubtitle: {
    fontSize: 9,
    color: "#A0A0B2",
    fontWeight: 500,
    whiteSpace: "nowrap",
  },
  trustStripDivider: {
    width: 1,
    height: 20,
    background: "#d6d2e7",
    flexShrink: 0,
  },
  heroCard: {
    marginTop: 12,
    borderRadius: 16,
    background: "linear-gradient(135deg, var(--primary) 0%, var(--primary) 100%)",
    minHeight: 148,
    padding: "10px 10px 10px",
    display: "flex",
    alignItems: "stretch",
    justifyContent: "space-between",
    gap: 8,
    overflow: "hidden",
    position: "relative",
  },
  heroCardLeft: {
    width: "62%",
    display: "flex",
    flexDirection: "column",
    color: "#fff",
    zIndex: 2,
  },
  heroCardTitle: {
    margin: 0,
    fontSize: "clamp(0.93rem, 2.35vw, 1.14rem)",
    lineHeight: 1.12,
    letterSpacing: "-0.4px",
    fontWeight: 800,
  },
  heroCardSubtitle: {
    marginTop: 5,
    marginBottom: 0,
    fontSize: "clamp(0.62rem, 1.22vw, 0.75rem)",
    color: "rgba(255,255,255,0.9)",
    lineHeight: 1.26,
    fontWeight: 500,
  },
  heroFeatureRow: {
    marginTop: 7,
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  heroFeaturePill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 7px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.11)",
    color: "#fff",
    fontSize: 9.4,
    fontWeight: 600,
  },
  heroCtaButton: {
    marginTop: 7,
    border: "none",
    borderRadius: 999,
    background: "#ffffff",
    color: "#3b20b5",
    padding: "6px 11px",
    fontSize: 11,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    width: "fit-content",
    cursor: "pointer",
  },
  heroCardRight: {
    width: "38%",
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    zIndex: 2,
  },
  heroCardImage: {
    width: "100%",
    maxWidth: 154,
    objectFit: "contain",
    filter: "drop-shadow(0 10px 14px rgba(0, 0, 0, 0.24))",
  },
  quickActions: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    gap: 12,
  },
  actionCard: {
    textAlign: "center",
    border: "none",
    borderRadius: 12,
    padding: "10px 14px",
    flex: 1,
    backdropFilter: "blur(6px)",
    transition: "all 0.2s",
    cursor: "pointer",
    background: "linear-gradient(20deg, var(--surface), var(--accent))",
  },
  actionText: {
    marginTop: 6,
    color: "white",
    fontWeight: 500,
    fontSize: 13
  },
  content: {
    padding: '20px 0',
  },
  section: {
    marginTop: 24,
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--on-bg)",
  },
  seeAllButton: {
    background: 'none',
    border: 'none',
    color: 'var(--primary)',
    fontSize: '14px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    cursor: 'pointer',
  },
  categoriesGrid: {
    display: "flex",
    overflowX: "auto",
    gap: 12,
    padding: "0",
    scrollSnapType: "x mandatory",
  },
  categoryCard: {
    position: "relative",
    flex: "0 0 80px",
    scrollSnapAlign: "start",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    aspectRatio: "1 / 1",
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(77, 30, 126, 0.09)",
    border: "1px solid #f0ecf7",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  categoryImageWrapper: {
    width: "100%",
    height: "50%",
  },
  categoryImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  categoryLabel: {
    fontSize: 11,
    fontWeight: "700",
    textAlign: "center",
    color: "#18141f",
    marginTop: 6,
    padding: "0 6px",
    lineHeight: 1.2,
    wordWrap: "break-word",
    overflow: "visible",
    whiteSpace: "normal",
  },
  productsRowsWrap: {
    display: "grid",
    gap: "10px",
  },
  productsRow: {
    display: "flex",
    overflowX: "auto",
    gap: "12px",
    paddingBottom: "10px",
    scrollSnapType: "x mandatory",
    WebkitOverflowScrolling: "touch",
  },
  emptyProductsState: {
    width: '100%',
    padding: '16px 14px',
    borderRadius: 14,
    background: '#ffffff',
    border: '1px solid #ece7f4',
    color: '#746d7e',
    fontSize: 12,
    fontWeight: 500,
  },
  productCard: {
    flex: "0 0 164px",
    minWidth: "164px",
    maxWidth: "164px",
    scrollSnapAlign: "start",
    backgroundColor: "transparent",

    padding: 0,
    boxShadow: "none",
    border: "none",
    cursor: "pointer",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  prescriptionBadge: {
    position: 'absolute',
    top: 8,
    left: 8,
    padding: '3px 8px',
    borderRadius: 10,
    color: '#fff',
    display : "none",
    fontSize: 9,
    fontWeight: 700,
    zIndex: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  productImageContainer: {
    position: 'relative',
    height: '200px',
    overflow: 'hidden',
    borderRadius: '18px',
    background: '#ededf0',
  },
  productImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  cartButton: {
    position: 'absolute',
    bottom: '8px',
    right: '8px',
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: 'white',
    border: 'solid 1.5px var(--primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.22)',
    color: 'var(--primary)',
  },
  productInfo: {
    marginTop : "5px",
    marginBottom: "2px",
    padding: '0 0 10',
    lineHeight: '1.3',
  },
  productName: {
    display: 'block',
    width: '100%',
    fontSize: '13px',
    fontWeight: '200',
    color: '#18141f',
    margin: '0',
    lineHeight: '1.3',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
  },
  productMeta: {
    fontSize: '11px',
    color: '#8f899a',
    margin: '2px 0 4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  currentPrice: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1f1a28',
    marginLeft: 0,
  },
  originalPrice: {
    fontSize: '11px',
    marginLeft: 0,
    color: '#9b93ab',
    textDecoration: 'line-through',
  },
  buyButton: {
    width: '100%',
    padding: '4px',
    background: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  orderHeader: {
    cursor: "pointer",
    background: "linear-gradient(180deg, #ffffff, #f8f7fb)",
    padding: "13px 16px",
    borderRadius: 14,
    border: "1px solid #ece7f4",
    boxShadow: "0 6px 16px rgba(52, 31, 98, 0.07)",
    color: "#201a2b",
    fontSize: 13,
    fontWeight: 500,
    transition: "background 0.2s, transform 0.1s",
  },
  orderHeaderTitle: {
    fontWeight: 700,
    color: "#1d1729",
    fontSize: 13,
  },
  orderHeaderToggle: {
    color: "var(--primary)",
    fontWeight: 700,
    fontSize: 12,
  },
  orderHeaderTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  statusSummary: {
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  orderStatusPill: {
    fontSize: 10.5,
    fontWeight: 700,
    padding: "4px 8px",
    borderRadius: 999,
    border: "1px solid rgba(0,0,0,0.04)",
  },
  orderSectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: 12,
    paddingLeft: 4,
    color: "#2a2238",
  },
  ordersList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  orderItem: {
    border: "1px solid #ece7f4",
    borderRadius: 14,
    padding: 12,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    background: "#ffffff",
    boxShadow: "0 6px 16px rgba(52, 31, 98, 0.06)",
  },
  orderItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  orderDate: {
    margin: 0,
    fontSize: "11px",
    color: "#7a7388",
  },
  orderStatus: {
    padding: "4px 8px",
    borderRadius: 999,
    color: "#fff",
    fontSize: 10.5,
    fontWeight: 700,
  },
  orderItems: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  orderItemRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    paddingBottom: 10,
    borderBottom: "1px solid #f0ecf7",
  },
  orderItemImageWrapper: {
    width: 60,
    height: 60,
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#f3f0f8",
    flexShrink: 0,
  },
  orderItemImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  orderItemDetails: {
    flex: 1,
    minWidth: 0,
  },
  orderItemName: {
    margin: "0 0 4px 0",
    fontSize: 14,
    fontWeight: 700,
    color: "#1f1a33",
  },
  orderItemSubtext: {
    margin: "0 0 4px 0",
    fontSize: 12,
    color: "#746d7e",
    fontWeight: 500,
  },
  orderItemPrice: {
    margin: 0,
    fontSize: 14,
    fontWeight: 700,
    color: "#1f1a33",
  },
  orderItemControl: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
  },
  orderItemQty: {
    width: 36,
    height: 32,
    borderRadius: 8,
    border: "1px solid #e9e6f1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 600,
    color: "#1f1a33",
    backgroundColor: "#fff",
  },
  orderItemAction: {
    width: 36,
    height: 32,
    borderRadius: 50,
    border: "none",
    background: "var(--primary)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
  },
  orderItemRemove: {
    background: "none",
    border: "none",
    color: "var(--primary)",
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    flexShrink: 0,
    padding: 0,
  },
  cartItemCard: {
    border: "1px solid #ece7f4",
    borderRadius: 14,
    padding: "12px 14px",
    background: "#ffffff",
    boxShadow: "0 2px 8px rgba(52, 31, 98, 0.05)",
    marginBottom: 12,
  },
  cartItemContent: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  cartItemImage: {
    width: 70,
    height: 70,
    borderRadius: 10,
    overflow: "hidden",
    backgroundColor: "#f3f0f8",
    flexShrink: 0,
  },
  cartItemInfo: {
    flex: 1,
    minWidth: 0,
  },
  cartItemName: {
    margin: "0 0 4px 0",
    fontSize: 14,
    fontWeight: 700,
    color: "#1f1a33",
    lineHeight: 1.2,
  },
  cartItemCategory: {
    margin: "0 0 6px 0",
    fontSize: 12,
    color: "#746d7e",
    fontWeight: 500,
  },
  cartItemPrice: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: "#1f1a33",
  },
  cartItemActions: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  quantityControl: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 36,
    borderRadius: 8,
    border: "1px solid #e9e6f1",
    background: "#ffffff",
  },
  quantityText: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1f1a33",
  },
  addButton: {
    width: 40,
    height: 36,
    borderRadius: 50,
    border: "none",
    background: "var(--primary)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    flexShrink: 0,
    transition: "transform 0.2s",
  },
  cartItemFooter: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: "1px solid #f0ecf7",
  },
  removeButton: {
    background: "none",
    border: "none",
    color: "var(--primary)",
    fontSize: 12,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
    padding: 0,
  },
  carouselContainer: {
    position: "relative",
    padding: 0,
    marginTop: 18,
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 10px 24px rgba(91, 33, 182, 0.22)",
  },
  promoCarousel: {
    display: "flex",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    gap: 0,
    width: "100%",
    scrollBehavior: "smooth",
    msOverflowStyle: "none",
    scrollbarWidth: "none",
  },
  promoBanner: {
    flex: "0 0 100%",
    scrollSnapAlign: "start",
    borderRadius: 14,
    overflow: "hidden",
    cursor: "pointer",
    height: 160,
  },
  promoImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  arrowButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    background: "rgba(0,0,0,0.4)",
    border: "none",
    borderRadius: "50%",
    padding: 8,
    color: "#fff",
    cursor: "pointer",
    zIndex: 10,
  },
  leftArrow: {
    left: 8,
  },
  rightArrow: {
    right: 8,
  },
  promoDotsWrap: {
    position: "absolute",
    left: "50%",
    bottom: 10,
    transform: "translateX(-50%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 6,
    marginTop: 0,
    zIndex: 11,
  },
  promoDot: {
    display: "inline-block",
    width: 6,
    height: 6,
    borderRadius: 999,
    background: "#d8c8f5",
    transition: "width 0.25s ease, background 0.25s ease",
    cursor: "pointer",
  },
  promoDotActive: {
    width: 22,
    background: "#d6b8ff",
  },
  safetyBanner: {
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    color: "#374151",
    marginTop: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  safetyContent: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  safetyIcon: {
    background: "inherit",
    color: "var(--primary)",
    borderRadius: "50%",
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  safetyTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 600,
  },
  safetyText: {
    margin: 0,
    fontSize: 13,
    color: "#6B7280",
    lineHeight: 1.4,
  },
  learnMoreButton: {
    background: "var(--primary)",
    color: "white",
    border: "none",
    borderRadius: 8,
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.2s",
  },
  pharmaciesGrid: {
    display: "flex",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    gap: "16px",
    paddingBottom: "8px",
  },
  pharmacyCard: {
    flex: "0 0 84%",
    maxWidth: 320,
    scrollSnapAlign: "start",
    borderRadius: 18,
    backgroundColor: "var(--surface)",
    overflow: "hidden",
    boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
  },
  pharmacyLogo: {
    width: "100%",
    height: 178,
    overflow: "hidden",
    position: "relative",
  },
  pharmacyLogoImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  verifiedBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    background: "var(--primary)",
    color: "#fff",
    borderRadius: "50%",
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  pharmacyName: {
    fontSize: 15,
    fontWeight: 700,
    marginTop: 2,
    marginBottom: 6,
    textAlign: "center",
    color: "#202125",
    lineHeight: 1.2,
  },
  pharmacyInfoPanel: {
    background: "#efefef",
    padding: "12px 14px 14px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  pharmacyRating: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    color: "#2a2b30",
  },
  ratingText: {
    fontSize: 13,
    fontWeight: 700,
    color: "#2a2b30",
  },
  reviewsText: {
    fontSize: 13,
    color: "#2a2b30",
  },
  deliveryBadge: {
    marginTop: 8,
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 13,
    fontWeight: 700,
    color: "var(--primary)",
  },
  productCardSkeleton: {
    flex: '0 0 164px',
    background: '#f7f7f8',
    borderRadius: '14px',
    padding: '10px',
    border: '1px solid #efedf2',
  },
  skeletonImage: {
    width: '100%',
    height: '122px',
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: '0',
    marginBottom: '12px',
  },
  skeletonText: {
    height: '12px',
    background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
    backgroundSize: '200% 100%',
    animation: 'shimmer 1.5s infinite',
    borderRadius: '6px',
    marginBottom: '8px',
  },
};

// Add CSS animations to index.css or inline
const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  .trust-strip-scroll::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }
`;
document.head.appendChild(styleTag);